import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import classes from './ItemDetail.module.css';
import { db } from '../../config';
import { doc, updateDoc } from 'firebase/firestore';
import { UserContext } from '../../store/user-context';
import { DEFAULTVALUES } from '../../config';
import { getItemCategories } from '../../config/helpers';
import parse from 'html-react-parser';
import BorrowScreen from '../Share/BorrowScreen';
import HirablePopover from './HirablePopover';

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M19 12H5M5 12L11 18M5 12L11 6" stroke="#2c3030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M3 6H21M8 6V4H16V6M19 6L18 20H6L5 6" stroke="#2c3030" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 5V19M5 12H19" stroke="#2c3030" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const MinusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M5 12H19" stroke="#2c3030" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const QuantityStepper = ({ value, onChange }) => (
  <div className={classes.stepperControls}>
    <button className={classes.stepperBtn} onClick={() => onChange(Math.max(0, value - 1))}>
      <MinusIcon />
    </button>
    <span className={classes.stepperValue}>{value}</span>
    <button className={classes.stepperBtn} onClick={() => onChange(value + 1)}>
      <PlusIcon />
    </button>
  </div>
);

// Same borrowStatus values NeedScreen's list-row badge reads — kept in sync
// there so the two never drift into showing different labels for the same
// item.
const getStatusBadge = (item) => {
  if (item?.borrowStatus === 'offered') {
    return {
      label: `Offered${item.offeredBy ? ` by ${item.offeredBy}` : ''}`,
      className: classes.badgeOffered,
    };
  }
  if (item?.borrowStatus === 'requested') {
    return { label: 'Requested', className: classes.badgeRequested };
  }
  if (item?.borrowStatus === 'hired' || item?.borrowStatus === 'borrow-pending') {
    return { label: 'Hired', className: classes.badgeHired };
  }
  return null;
};

const ItemDetail = ({ item: initialItem, group, onBack }) => {
  const UserCtx = useContext(UserContext);

  // The caller (NeedScreen/PackScreen/Checklist) hands us a snapshot of the
  // item taken at the moment it was tapped, held in that screen's own
  // `selectedItem` state — it never gets refreshed as `group` updates from
  // Firestore's live listener. That's harmless for most of this screen
  // (own/need quantities, notes) since only the current user edits those
  // from here, but borrowStatus can change from someone else's action
  // (a mate accepting a borrow request) while this screen is still open, so
  // it's re-derived from the live `group.items` on every render instead of
  // trusting the frozen prop.
  const item = useMemo(() => {
    if (!group?.items || !initialItem?.id) return initialItem;
    return group.items.find(i => i.id === initialItem.id) || initialItem;
  }, [group?.items, initialItem]);
  const [ownQty, setOwnQty] = useState(() => {
    if (item?.ownQuantity !== undefined) return item.ownQuantity;
    return (item?.organise || []).filter(o => o.method === 'Bring').length;
  });
  const [needQty, setNeedQty] = useState(() => {
    if (item?.needQuantity !== undefined) return item.needQuantity;
    return (item?.organise || []).filter(o => o.method === 'Borrow' || o.method === 'Hire').length;
  });
  const [notes, setNotes] = useState(item?.notes || '');
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(item?.name || '');
  const nameInputRef = useRef(null);
  const [proTip, setProTip] = useState(null);
  const [hireUrl, setHireUrl] = useState(null);
  const [isHirable, setIsHirable] = useState(false);
  const [festivalName, setFestivalName] = useState(null);
  const [showHirePopover, setShowHirePopover] = useState(false);
  const hireBadgeRef = useRef(null);
  const [showBorrowScreen, setShowBorrowScreen] = useState(false);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [categoryMenuStyle, setCategoryMenuStyle] = useState(null);
  const categoryTriggerRef = useRef(null);
  const categoryMenuRef = useRef(null);

  // ItemDetail renders inside the same shared `.content` scroll container as
  // the list screens (My Gear/Need/Pack) — Root.js swaps children in place
  // rather than mounting a fresh scrollable wrapper per screen, so without
  // this it opens at whatever scroll position the list was left at instead
  // of at the top. Deliberately mount-only, no unmount cleanup: returning to
  // the list (onBack) should land back wherever the user was — same
  // category, same scroll position — not jump to the top of the list.
  useEffect(() => {
    window.scrollTo(0, 0);
    const contentEl = document.querySelector('.content');
    if (contentEl) contentEl.scrollTop = 0;
  }, []);

  // Get pro tip from festival items
  useEffect(() => {
    if (!item) return;
    const festival = UserCtx?.festival || group?.festival;
    const defaultItems = UserCtx?.itemInfo;
    console.log('Festival:', festival?.name, 'Items:', festival?.items?.length, 'Default:', defaultItems?.length);
    // An empty array is still truthy, so a festival with no customised
    // items (useDefaultItems: true and nothing in its own items array) must
    // fall through to defaultItems explicitly — see the same fix in
    // GearCard.js.
    const itemInfoList = (festival?.items?.length ? festival.items : defaultItems) || [];
    const itemName = item.name?.toLowerCase();

    let match = itemInfoList.find(i => i.name?.toLowerCase() === itemName);
    if (!match) {
      match = itemInfoList.find(i => {
        const aka = i.aka?.toLowerCase().split(',').map(s => s.trim());
        return aka?.includes(itemName);
      });
    }

    if (match) {
      let tip = match.tip || null;
      if (tip && !DEFAULTVALUES.includes(tip)) {
        try { tip = parse(tip); } catch(e) {}
        setProTip(tip);
      }
      // The Hirable badge below only reflects hire — hireUrl only gates
      // whether the Hire action button actually opens somewhere (see the
      // render condition further down).
      setIsHirable(match.hire || false);

      // Backfill hire/tip onto the stored item — items added manually
      // before these fields were carried through at add time never got
      // them, so this is a one-time write per item.
      const missingFields = item.hire === undefined && item.tip === undefined;
      if (missingFields && group?.id && item?.id && group?.items) {
        const itemIndex = group.items.findIndex(i => i.id === item.id);
        if (itemIndex !== -1) {
          const updatedItems = [...group.items];
          updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            tip: match.tip || null,
            hire: match.hire || false,
          };
          updateDoc(doc(db, 'groups', group.id), { items: updatedItems }).catch(err => {
            console.log('Error backfilling item catalog fields:', err);
          });
        }
      }
    }

    if (festival?.hireurl) setHireUrl(festival.hireurl);
    if (festival?.name) setFestivalName(festival.name);
  }, [item, group, UserCtx]);

  const saveToFirebase = async (field, value) => {
    if (!group?.id || !item?.id) return;
    setSaving(true);
    try {
      const itemIndex = group.items.findIndex(i => i.id === item.id);
      if (itemIndex === -1) return;
      const updatedItems = [...group.items];
      updatedItems[itemIndex] = { ...updatedItems[itemIndex], [field]: value };
      const groupRef = doc(db, 'groups', group.id);
      await updateDoc(groupRef, { items: updatedItems });
    } catch (err) {
      console.log('Error saving:', err);
    }
    setSaving(false);
  };

  const deleteItem = async () => {
    if (!group?.id || !item?.id) return;
    setSaving(true);
    try {
      const updatedItems = group.items.filter(i => i.id !== item.id);
      await updateDoc(doc(db, 'groups', group.id), { items: updatedItems });
      onBack();
    } catch (err) {
      console.log('Error deleting item:', err);
    }
    setSaving(false);
    setConfirmDelete(false);
  };

  const handleOwnChange = (val) => {
    if (val > ownQty) { setShowToast(true); setTimeout(() => setShowToast(false), 2000); }
    setOwnQty(val);
    saveToFirebase('ownQuantity', val);
  };
  const handleNeedChange = (val) => { setNeedQty(val); saveToFirebase('needQuantity', val); };
  const handleNotesBlur = () => { saveToFirebase('notes', notes); };

  // Position is computed in viewport coordinates (getBoundingClientRect)
  // and the menu is rendered via a portal with position: fixed — simplest
  // way to guarantee it floats above ItemDetail's own scroll container and
  // never gets clipped by an ancestor's overflow, matching the app's
  // existing pattern for ConfirmDialog further down. Flips above/aligns
  // right when there isn't room below/to the right, so it never runs off
  // screen regardless of where the trigger sits.
  const openCategoryMenu = () => {
    const trigger = categoryTriggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const MENU_WIDTH = 220;
    const MENU_MAX_HEIGHT = 280;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const style = {};
    if (rect.left + MENU_WIDTH > viewportWidth - 8) {
      style.right = Math.max(8, viewportWidth - rect.right);
    } else {
      style.left = rect.left;
    }
    const spaceBelow = viewportHeight - rect.bottom;
    if (spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow) {
      style.bottom = viewportHeight - rect.top + 6;
    } else {
      style.top = rect.bottom + 6;
    }
    setCategoryMenuStyle(style);
    setCategoryMenuOpen(true);
  };

  const handleCategorySelect = (cat) => {
    setCategoryMenuOpen(false);
    if (cat !== currentCategory) saveToFirebase('categories', cat);
  };

  useEffect(() => {
    if (!categoryMenuOpen) return;
    const handleClickOutside = (e) => {
      if (
        categoryMenuRef.current && !categoryMenuRef.current.contains(e.target) &&
        categoryTriggerRef.current && !categoryTriggerRef.current.contains(e.target)
      ) {
        setCategoryMenuOpen(false);
      }
    };
    // Closes when the page/scroll-container behind the menu moves (so a
    // fixed-position menu doesn't drift away from its trigger) — but not
    // when the scroll originates inside the menu's own scrollable list,
    // which is exactly how a long category list is meant to be browsed.
    const handleScroll = (e) => {
      if (categoryMenuRef.current && categoryMenuRef.current.contains(e.target)) return;
      setCategoryMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [categoryMenuOpen]);

  useEffect(() => {
    if (editingName && nameInputRef.current) nameInputRef.current.focus();
  }, [editingName]);

  const commitName = () => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== item.name) saveToFirebase('name', trimmed);
    else setNameValue(item.name);
    setEditingName(false);
  };

  const cancelName = () => {
    setNameValue(item.name);
    setEditingName(false);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitName(); }
    if (e.key === 'Escape') cancelName();
  };

  const handleGotIt = async () => {
    if (!group?.id || !item?.id) return;
    const newOwnQty = ownQty + needQty;
    setSaving(true);
    try {
      const itemIndex = group.items.findIndex(i => i.id === item.id);
      if (itemIndex !== -1) {
        const updatedItems = [...group.items];
        // Getting the item resolves whatever borrow request/offer was open
        // on it — leaving borrowStatus set would show a stale "Requested"/
        // "Offered" badge on an item that's now fully sorted.
        const { borrowStatus, offeredBy, ...rest } = updatedItems[itemIndex];
        updatedItems[itemIndex] = { ...rest, ownQuantity: newOwnQty, needQuantity: 0 };
        await updateDoc(doc(db, 'groups', group.id), { items: updatedItems });
        setShowToast(true);
        setTimeout(onBack, 1500);
      }
    } catch (err) {
      console.log('Error saving:', err);
    }
    setSaving(false);
  };

  // Manual escape hatch — the item is still needed, but the borrow
  // request/offer itself is stale or wrong (e.g. asked before finding it
  // elsewhere, or a mate's offer fell through) and should clear back to no
  // badge without also touching own/need quantities the way "Got it" does.
  const handleClearStatus = async () => {
    if (!group?.id || !item?.id) return;
    setSaving(true);
    try {
      const itemIndex = group.items.findIndex(i => i.id === item.id);
      if (itemIndex !== -1) {
        const updatedItems = [...group.items];
        const { borrowStatus, offeredBy, ...rest } = updatedItems[itemIndex];
        updatedItems[itemIndex] = rest;
        await updateDoc(doc(db, 'groups', group.id), { items: updatedItems });
      }
    } catch (err) {
      console.log('Error clearing status:', err);
    }
    setSaving(false);
  };

  const statusBadge = getStatusBadge(item);
  const totalQty = ownQty + needQty;
  const currentCategory = getItemCategories(item)[0] || '';
  const categoryOptions = group?.categories || [];
  // Covers a stale/renamed category that's since been removed from
  // group.categories — without this the <select> would silently jump to
  // whatever option happens to be first, quietly moving the item.
  const categorySelectOptions = currentCategory && !categoryOptions.includes(currentCategory)
    ? [currentCategory, ...categoryOptions]
    : categoryOptions;

  if (showBorrowScreen) return (
    <BorrowScreen group={group} onClose={() => setShowBorrowScreen(false)} />
  );

  return (
    <div className={classes.container}>

      {/* Header */}
      <div className={classes.header}>
        <button className={classes.backBtn} onClick={onBack}>
          <BackIcon />
        </button>
        <div className={classes.headerRight}>
          {editingName ? (
            <input
              ref={nameInputRef}
              className={classes.itemTitleInput}
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onBlur={commitName}
              onKeyDown={handleNameKeyDown}
              spellCheck="false"
            />
          ) : (
            <button className={classes.itemTitleBtn} onClick={() => setEditingName(true)}>
              <h1 className={classes.itemTitle}>
                {nameValue}
                {totalQty > 1 && <span className={classes.itemQty}> ×{totalQty}</span>}
              </h1>
              <span className={classes.editHint}>✏️</span>
            </button>
          )}
          {isHirable && (
            <button
              ref={hireBadgeRef}
              className={`${classes.hirableBadge} ${classes.hirableBadgeTappable}`}
              onClick={() => setShowHirePopover(v => !v)}
            >
              Hirable
            </button>
          )}
          {showHirePopover && (
            <HirablePopover
              anchorRef={hireBadgeRef}
              festivalName={festivalName}
              hireUrl={hireUrl}
              onClose={() => setShowHirePopover(false)}
            />
          )}
        </div>
        <button className={classes.deleteBtn} onClick={() => setConfirmDelete(true)} aria-label="Delete item">
          <DeleteIcon />
        </button>
      </div>

      {confirmDelete && ReactDOM.createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(44,48,48,0.55)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px'
        }}>
          <div style={{
            backgroundColor: '#ffffff', borderRadius: '20px', padding: '28px 24px',
            width: '100%', maxWidth: '300px', display: 'flex', flexDirection: 'column',
            gap: '20px', boxShadow: '0 8px 32px rgba(44,48,48,0.18)'
          }}>
            <p style={{
              fontFamily: 'Poppins, Arial, sans-serif', fontSize: '15px',
              color: '#2c3030', textAlign: 'center', lineHeight: '1.5', margin: 0
            }}>Remove <strong>{nameValue}</strong> from your list?</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setConfirmDelete(false)} style={{
                flex: 1, height: '48px', borderRadius: '99px', backgroundColor: '#f5f7f7',
                border: '1px solid #e0e1e1', fontFamily: 'Poppins, Arial, sans-serif',
                fontSize: '14px', fontWeight: 600, color: '#565959', cursor: 'pointer'
              }}>Cancel</button>
              <button onClick={deleteItem} disabled={saving} style={{
                flex: 1, height: '48px', borderRadius: '99px', backgroundColor: '#fc4b4e',
                border: 'none', fontFamily: 'Poppins, Arial, sans-serif',
                fontSize: '14px', fontWeight: 600, color: '#ffffff', cursor: 'pointer',
                opacity: saving ? 0.6 : 1
              }}>{saving ? 'Removing...' : 'Remove'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className={classes.content}>

        {/* Borrow/hire status */}
        {statusBadge && (
          <div className={classes.statusRow}>
            <span className={`${classes.statusBadge} ${statusBadge.className}`}>{statusBadge.label}</span>
            <button className={classes.statusClearBtn} onClick={handleClearStatus} disabled={saving}>
              Clear
            </button>
          </div>
        )}

        {/* Pro tip */}
        {proTip && (
          <div className={classes.proTipCard}>
            <div className={classes.proTipHeader}>
              <span className={classes.proTipIcon}>💡</span>
              <span className={classes.proTipTitle}>Pro tip!</span>
            </div>
            <div className={classes.proTipText}>{proTip}</div>
          </div>
        )}

        {/* Quantity section */}
        <div className={classes.quantityCard}>
          <div className={classes.quantityRow}>
            <span className={classes.quantityLabel}>Own</span>
            <QuantityStepper
              value={ownQty}
              onChange={handleOwnChange}
            />
          </div>
          <div className={classes.divider} />
          <div className={classes.quantityRow}>
            <span className={classes.quantityLabel}>Need to get</span>
            <QuantityStepper
              value={needQty}
              onChange={handleNeedChange}
            />
          </div>
        </div>

        {/* Category */}
        <div className={classes.quantityCard}>
          <div className={classes.quantityRow}>
            <span className={classes.quantityLabel}>Category</span>
            <button
              ref={categoryTriggerRef}
              type="button"
              className={classes.categoryTrigger}
              onClick={() => (categoryMenuOpen ? setCategoryMenuOpen(false) : openCategoryMenu())}
              disabled={categorySelectOptions.length === 0}
            >
              <span className={classes.categoryTriggerLabel}>{currentCategory || 'Select'}</span>
              <span className={classes.categoryTriggerChevron}>▾</span>
            </button>
          </div>
        </div>

        {categoryMenuOpen && ReactDOM.createPortal(
          <div ref={categoryMenuRef} className={classes.categoryMenu} style={categoryMenuStyle}>
            {categorySelectOptions.map(cat => {
              const selected = cat === currentCategory;
              return (
                <button
                  key={cat}
                  type="button"
                  className={classes.categoryMenuRow}
                  onClick={() => handleCategorySelect(cat)}
                >
                  <span className={selected ? classes.categoryMenuLabelSelected : classes.categoryMenuLabel}>
                    {cat}
                  </span>
                  {selected && <span className={classes.categoryMenuCheck}>✓</span>}
                </button>
              );
            })}
          </div>,
          document.body
        )}

        {/* Notes */}
        <div className={classes.notesCard}>
          <textarea
            className={classes.notesInput}
            placeholder="Enter notes/comments"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onBlur={handleNotesBlur}
            rows={4}
          />
        </div>

        {/* Action buttons */}
        {needQty > 0 && (
          <div className={classes.actionRow}>
            <button
              className={classes.actionBtn}
              onClick={() => setShowBorrowScreen(true)}
            >
              Borrow
            </button>
            <button
              className={classes.actionBtn}
              onClick={() => hireUrl && window.open(hireUrl, '_blank')}
            >
              Hire
            </button>
            <button className={classes.actionBtn} onClick={handleGotIt}>
              Sorted
            </button>
          </div>
        )}

      </div>

      {showToast && (
        <div className={classes.toast}>Added to your pack list ✓</div>
      )}

    </div>
  );
};

export default ItemDetail;
